import { AsyncButton, loadWrapper } from "aic";
import { useAppSettings, useHierarchyContext, useTlfContext } from "contexts/AppSettingsContext";
import { eCommentReviewStatus } from "functions/api/comments/interface";
import {
  PatchAllCommentDetails,
  patchAllCommentStatus,
} from "functions/api/comments/patchAllCommentStatus";
import { eAccessRights } from "functions/api/enums";
import { IRequestStatus } from "functions/api/interface";
import { CommentableEntityType } from "functions/api/notifications/interface";
import { MouseEventHandler, ReactNode, useCallback, useMemo, useState } from "react";
import { Check2All } from "react-bootstrap-icons";
import { useComments } from "./useComments";

interface ResolveCommentButtonProps {
  activeContext: CommentableEntityType;
}

/**
 * Resolve all comments button.
 *
 * @param props - Component props.
 * @param props.activeContext - Active comment view context.
 */
export const ResolveAllCommentsButton = ({
  activeContext,
}: ResolveCommentButtonProps): ReactNode => {
  const appSettings = useAppSettings();
  const hierarchyContext = useHierarchyContext();
  const hierarchy = hierarchyContext?.hierarchy;
  const tlfContext = useTlfContext();
  const tlfData = tlfContext.tlfData;

  const { setComments } = useComments(activeContext);

  const canResolveAll = useMemo(() => {
    if (activeContext === "Hierarchy") {
      return (
        hierarchyContext?.hierarchy?.comments?.some(
          (c) => c.commentReviewStatus === eCommentReviewStatus.open,
        ) ?? false
      );
    } else {
      return (
        tlfContext.tlfData?.comments?.some(
          (c) => c.commentReviewStatus === eCommentReviewStatus.open,
        ) ?? false
      );
    }
  }, [activeContext, hierarchyContext, tlfContext]);

  const thisCommentRequestDto: PatchAllCommentDetails | null = useMemo(() => {
    if (activeContext === "Hierarchy") {
      if (!hierarchy) {
        return null;
      }
      return {
        activeContext: "Hierarchy",
        hierarchyId: hierarchy.id,
        newStatus: eCommentReviewStatus.resolved,
      };
    } else {
      if (!tlfData) {
        return null;
      }
      return {
        activeContext: "TlfItem",
        id: tlfData.id,
        azsolId: tlfData.azsolId,
        version: tlfData.version,
        newStatus: eCommentReviewStatus.resolved,
      };
    }
  }, [activeContext, hierarchy, tlfData]);

  const [patchCommentStatusRequest, setPatchCommentStatusRequest] = useState<
    IRequestStatus<PatchAllCommentDetails>
  >({
    requesting: false,
    error: false,
  });

  const handleClick: MouseEventHandler<HTMLButtonElement> = useCallback(
    async (e) => {
      e.preventDefault();
      e.stopPropagation();
      /* istanbul ignore else */
      if (thisCommentRequestDto) {
        const request = await loadWrapper(
          patchAllCommentStatus,
          thisCommentRequestDto,
          { ...patchCommentStatusRequest, requestedId: undefined, error: false },
          setPatchCommentStatusRequest,
          appSettings.backEndUrl,
        );
        /* istanbul ignore else */
        if (request?.success) {
          setComments(request.data, true);
        }
      }
    },
    [appSettings.backEndUrl, patchCommentStatusRequest, setComments, thisCommentRequestDto],
  );

  return !thisCommentRequestDto ? null : (
    <AsyncButton
      id={`resolve-all-comments-${activeContext}`}
      className="resolve-all-button ms-2"
      title="Resolve all"
      size="sm"
      variant="success"
      disabled={
        !hierarchyContext?.accessRights.includes(eAccessRights.MaintainHierarchyComments) ||
        !canResolveAll
      }
      requestStatus={patchCommentStatusRequest}
      onClick={handleClick}
    >
      <Check2All className="me-1" />
      Resolve all
    </AsyncButton>
  );
};

ResolveAllCommentsButton.displayName = "ResolveAllCommentsButton";
